import { Box, Button, TextField, CircularProgress, Typography, Divider, Grid } from '@mui/material';
import { Formik } from 'formik';
import React, { FC, useState, useCallback } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import BankingService from 'services/banking.service';
import { EPixOperationType } from 'types/banking';
import { formatCurrency, parseCurrency } from 'utils/banking';
import { formatTaxNumber } from 'utils/formatters';
import { cleanPixKey, isValidPixKey } from 'utils/pix';

type FlowProps = {
  onSuccess: () => void;
  onCancel: () => void;
  onSubmitting: (loading: boolean) => void;
  accountNumber?: string;
};

interface PixKeyInfo {
  key?: string;
  keyType?: string;
  accountHolder?: string;
  documentNumber?: string;
  accountNumber?: string;
  bankCode?: string;
  bankName?: string;
  isActive?: boolean;
  endToEndId?: string;
}

const pixKeyValidation = Yup.string()
  .required('Obrigatório')
  .test('valid-pix-format', 'Formato de chave PIX inválido', (value) => {
    return isValidPixKey(value || '');
  });

const RecipientInfo: FC<{ pixKeyInfo: PixKeyInfo }> = ({ pixKeyInfo }) => (
  <Box mb={3}>
    <Typography variant="body1" fontWeight={500} mb={2} sx={{ fontSize: '14px' }}>
      Destinatário
    </Typography>
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Grid container spacing={1.5}>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography color="grey.600" fontWeight={500} sx={{ fontSize: '14px' }}>
            Titular:
          </Typography>
          <Typography color="grey.800" fontWeight={500} sx={{ fontSize: '14px' }}>
            {pixKeyInfo.accountHolder || 'N/A'}
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography color="grey.600" fontWeight={500} sx={{ fontSize: '14px' }}>
            Documento:
          </Typography>
          <Typography color="grey.800" fontWeight={500} sx={{ fontSize: '14px' }}>
            {formatTaxNumber(pixKeyInfo.documentNumber)}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography color="grey.600" fontWeight={500} sx={{ fontSize: '14px' }}>
            {pixKeyInfo.bankName && pixKeyInfo.bankName !== pixKeyInfo.bankCode ? 'Banco:' : 'ISPB:'}
          </Typography>
          <Typography color="grey.800" fontWeight={500} sx={{ fontSize: '14px' }}>
            {pixKeyInfo.bankName && pixKeyInfo.bankName !== pixKeyInfo.bankCode ? pixKeyInfo.bankName : pixKeyInfo.bankCode || 'N/A'}
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography color="grey.600" fontWeight={500} sx={{ fontSize: '14px' }}>
            Tipo:
          </Typography>
          <Typography color="grey.800" fontWeight={500} sx={{ fontSize: '14px' }}>
            {pixKeyInfo.keyType || 'N/A'}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  </Box>
);

const PixByKey: FC<FlowProps> = ({ onSuccess, onCancel, onSubmitting, accountNumber = '' }) => {
  const bankingService = new BankingService();
  const { enqueueSnackbar } = useSnackbar();
  const [pixKeyInfo, setPixKeyInfo] = useState<PixKeyInfo | null>(null);
  const [pixKeyLoading, setPixKeyLoading] = useState(false);
  const [lastValidatedKey, setLastValidatedKey] = useState<string | null>(null);

  const handlePixKeyBlur = useCallback(
    async (pixKey: string, setFieldError: (field: string, message: string | undefined) => void) => {
      // Skip if same key as last validated
      if (pixKey === lastValidatedKey) {
        return;
      }

      // Validate format using the schema
      try {
        await pixKeyValidation.validate(pixKey);
      } catch (error) {
        setFieldError('pixKey', error instanceof Error ? error.message : 'Formato inválido');
        setPixKeyInfo(null);
        setLastValidatedKey(null);
        return;
      }

      // Clear previous errors
      setFieldError('pixKey', undefined);
      setPixKeyLoading(true);

      try {
        const response = await bankingService.getPixKeyInfo({
          pixKey: cleanPixKey(pixKey),
          accountNumber,
        });
        setPixKeyInfo(response);
        setLastValidatedKey(pixKey);
      } catch (error) {
        let errorMessage: string;

        if ((error as any)?.data?.detail) {
          errorMessage = 'Chave PIX não encontrada';
        } else if (error instanceof Error) {
          errorMessage = error.message;
        } else {
          errorMessage = 'Erro ao validar chave PIX';
        }
        setFieldError('pixKey', errorMessage);
        setPixKeyInfo(null);
      } finally {
        setPixKeyLoading(false);
      }
    },
    [lastValidatedKey, accountNumber, bankingService]
  );

  return (
    <Formik
      initialValues={{
        pixKey: '',
        amount: '',
        date: new Date().toLocaleDateString('en-CA'),
        description: '',
      }}
      validationSchema={Yup.object({
        pixKey: pixKeyValidation,
        amount: Yup.string().required('Obrigatório'),
        date: Yup.string().required('Obrigatório'),
      })}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        onSubmitting(true);

        try {
          await bankingService.createTransaction(EPixOperationType.PIX_KEY, {
            pixKey: values.pixKey,
            value: parseCurrency(values.amount),
            transferDate: values.date,
            description: values.description || undefined,
            ...(pixKeyInfo && {
              counterpartyName: pixKeyInfo.accountHolder,
              counterpartyTaxNumber: pixKeyInfo.documentNumber,
              counterpartyBankCode: pixKeyInfo.bankCode,
              counterpartyAccount: pixKeyInfo.accountNumber,
              endToEndId: pixKeyInfo.endToEndId,
            }),
          });

          resetForm();
          onSuccess();

          enqueueSnackbar('Transação PIX criada com sucesso.', { variant: 'success' });
        } catch (error) {
          enqueueSnackbar('Erro ao criar transação PIX. Tente novamente.', { variant: 'error' });
        } finally {
          onSubmitting(false);
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldError, handleSubmit, isValid, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            variant="standard"
            label="Chave Pix *"
            placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
            name="pixKey"
            value={values.pixKey}
            onChange={handleChange}
            onBlur={(e) => {
              handleBlur(e);
              handlePixKeyBlur(values.pixKey, setFieldError);
            }}
            error={Boolean(touched.pixKey && errors.pixKey)}
            helperText={touched.pixKey && errors.pixKey}
            sx={{ mb: 3 }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
            InputProps={{ sx: { fontSize: '14px' } }}
            disabled={isSubmitting || pixKeyLoading}
          />

          {pixKeyLoading && (
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Validando chave PIX...</Typography>
            </Box>
          )}

          {pixKeyInfo && <RecipientInfo pixKeyInfo={pixKeyInfo} />}

          <TextField
            fullWidth
            variant="standard"
            label="Valor (R$) *"
            placeholder="R$ 0,00"
            value={values.amount}
            onChange={(e) => setFieldValue('amount', formatCurrency(e.target.value))}
            error={Boolean(touched.amount && errors.amount)}
            helperText={touched.amount && errors.amount}
            sx={{ mb: 3 }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
            InputProps={{ sx: { fontSize: '14px' } }}
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            variant="standard"
            label="Data de transferência *"
            name="date"
            type="date"
            value={values.date}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.date && errors.date)}
            helperText={touched.date && errors.date}
            sx={{ mb: 3 }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
            inputProps={{ min: new Date().toLocaleDateString('en-CA'), sx: { fontSize: '14px' } }}
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            variant="standard"
            label="Descrição (Opcional)"
            placeholder="Pagamento de fornecedor"
            value={values.description}
            onChange={handleChange('description')}
            sx={{ mb: 4 }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
            InputProps={{ sx: { fontSize: '14px' } }}
            disabled={isSubmitting}
          />

          <Box display="flex" gap={2}>
            <Button fullWidth variant="outlined" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button fullWidth variant="contained" type="submit" disabled={isSubmitting || !isValid || pixKeyLoading || !pixKeyInfo}>
              {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Confirmar'}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default PixByKey;
